import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import io from 'socket.io-client';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';
const MEDIA_BASE_URL = API_URL.replace(/\/api\/?$/, '');

export default function App() {
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [connected, setConnected] = useState(false);
  const [screenCode, setScreenCode] = useState('');
  const [statusMessage, setStatusMessage] = useState('Connecting...');
  const [playCount, setPlayCount] = useState(0);
  const [testVideo, setTestVideo] = useState(null);
  const [testPlayCount, setTestPlayCount] = useState(0);
  const startedAtRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code') || process.env.REACT_APP_SCREEN_CODE || 'SCREEN001';
    const token = params.get('token') || process.env.REACT_APP_SCREEN_TOKEN;
    setScreenCode(code);

    const socket = io(SOCKET_URL, {
      auth: { token, screenCode: code },
      reconnection: true,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      setStatusMessage('Connected - waiting for playlist...');
    });

    socket.on('disconnect', () => {
      setConnected(false);
      setStatusMessage('Disconnected from server');
    });

    socket.on('connect_error', (error) => {
      setConnected(false);
      setStatusMessage(`Connection error: ${error.message}`);
    });

    socket.on('TEST_PLAYBACK_START', (data) => {
      setTestVideo(data.video || null);
      setTestPlayCount((prev) => prev + 1);
    });

    socket.on('TEST_PLAYBACK_STOP', () => {
      setTestVideo(null);
    });

    socket.on('PLAYLIST_UPDATED', (data) => {
      if (!data.playlistId) {
        setItems([]);
        setStatusMessage(data.message || 'No active schedule');
        return;
      }

      setItems(data.items || []);
      setCurrentIndex(0);
      setStatusMessage(`Playing playlist #${data.playlistId} (v${data.version})`);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const currentItem = items[currentIndex] || null;
  const videoUrl = currentItem ? `${MEDIA_BASE_URL}/uploads/videos/${currentItem.filename}` : null;
  const testVideoUrl = testVideo ? `${MEDIA_BASE_URL}/uploads/videos/${testVideo.filename}` : null;

  const handleStart = () => {
    startedAtRef.current = new Date();
    if (socketRef.current && currentItem) {
      socketRef.current.emit('PLAYBACK_STARTED', {
        videoId: currentItem.video_id,
        startedAt: startedAtRef.current.toISOString(),
      });
    }
  };

  const handleEnded = () => {
    if (socketRef.current && currentItem && startedAtRef.current) {
      const endedAt = new Date();
      const duration = Math.round((endedAt - startedAtRef.current) / 1000);
      socketRef.current.emit('PLAYBACK_FINISHED', {
        videoId: currentItem.video_id,
        startedAt: startedAtRef.current.toISOString(),
        endedAt: endedAt.toISOString(),
        duration,
      });
    }

    setPlayCount((prev) => prev + 1);
    if (items.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }
  };

  return (
    <div className="player-container">
      {testVideoUrl ? (
        <ReactPlayer
          key={`test-${testVideo.id}-${testPlayCount}`}
          url={testVideoUrl}
          playing
          muted
          width="100%"
          height="100%"
          onEnded={() => setTestPlayCount((prev) => prev + 1)}
          onError={(e) => setStatusMessage(`Playback error: ${e?.message || e}`)}
        />
      ) : videoUrl ? (
        <ReactPlayer
          key={`${currentItem.id}-${playCount}`}
          url={videoUrl}
          playing
          muted
          width="100%"
          height="100%"
          onStart={handleStart}
          onEnded={handleEnded}
          onError={(e) => setStatusMessage(`Playback error: ${e?.message || e}`)}
        />
      ) : (
        <div className="placeholder">
          <h2>{statusMessage}</h2>
        </div>
      )}

      {testVideo && <div className="test-mode-banner">TEST MODE - {testVideo.title}</div>}

      <div className="status-bar">
        <span>Screen: {screenCode}</span>
        <span>Socket: {connected ? 'Connected' : 'Disconnected'}</span>
        {testVideo ? (
          <span>Testing: {testVideo.title}</span>
        ) : (
          currentItem && (
            <span>
              {currentIndex + 1} / {items.length} - {currentItem.title}
            </span>
          )
        )}
      </div>
    </div>
  );
}
